// Shared pure geometry for DEM corridor routing — imported by both the main-thread
// client (which pre-fetches the corridor tiles) and the worker (which runs the A*).
// No DOM / Dexie / network here so it is safe to bundle into a Web Worker.
import { DEM_TILE_SIZE } from '../terrain/dem-config.js'

export type LL = [number, number] // [lng, lat]

export const DEM_Z = 16
export const TILE = DEM_TILE_SIZE // 512
export const WORLD_PX = TILE * 2 ** DEM_Z // global z16 pixel span (x and y)

// global z16 pixel <-> lng/lat (slippy-map / web-mercator); x and y share the scale
export const lngToGX = (lng: number): number => ((lng + 180) / 360) * WORLD_PX
export const latToGY = (lat: number): number =>
  (WORLD_PX * (1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI)) / 2
export const gxToLng = (gx: number): number => (gx / WORLD_PX) * 360 - 180
export const gyToLat = (gy: number): number => {
  const n = Math.PI - (2 * Math.PI * gy) / WORLD_PX
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

// meters per z16 pixel at a latitude (x == y in mercator)
export const metersPerPx = (lat: number): number =>
  (Math.cos((lat * Math.PI) / 180) * 2 * Math.PI * 6378137) / WORLD_PX

export const decodeTerrarium = (r: number, g: number, b: number): number =>
  r * 256 + g + b / 256 - 32768

export interface DemTuning {
  bufferM?: number // corridor half-width around the straight line (m)
  directness?: number // hours-per-km penalty on distance; bigger = straighter
}
export const DEM_DEFAULTS: Required<DemTuning> = { bufferM: 120, directness: 0.2 }

/** z16 tile key matching the corridor solver's `ty * 2**DEM_Z + tx`. */
export const tileKey = (tx: number, ty: number): number => ty * 2 ** DEM_Z + tx
